import React from 'react'
import {
    Box,
    Button,
    InputAdornment,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'

import { LeadSourceCounts } from '../../hooks/usePipelineCounts'
import { LeadSourcesChart } from './LeadSourcesChart'

export type LeadsRosterScope = 'in_leads' | 'everyone'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    header: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: theme.spacing(1.5),
    },
    tiles: {
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'flex-start',
        gap: theme.spacing(1),
        flex: '1 1 420px',
    },
    tile: {
        minWidth: 140,
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
            borderColor: theme.palette.primary.light,
        },
    },
    tileActive: {
        borderColor: theme.palette.primary.main,
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
        backgroundColor:
            theme.palette.type === 'dark'
                ? 'rgba(25, 118, 210, 0.12)'
                : 'rgba(25, 118, 210, 0.06)',
    },
    tileValue: {
        fontSize: '1.25rem',
        fontWeight: 700,
        fontFamily: 'monospace',
        lineHeight: 1.2,
        color: theme.palette.text.primary,
    },
    tileLabel: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    tileHint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    tools: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    search: {
        flex: '1 1 220px',
        minWidth: 220,
    },
    showing: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    showingValue: {
        fontWeight: 700,
        fontFamily: 'monospace',
        color: theme.palette.text.primary,
    },
}))

interface LeadsRosterHeaderProps {
    scope: LeadsRosterScope
    onScopeChange: (scope: LeadsRosterScope) => void
    inLeadsCount: number
    everyoneCount: number
    query: string
    onQueryChange: (value: string) => void
    selectedSources: string[]
    onSourcesChange: (sources: string[]) => void
    sourceCounts?: LeadSourceCounts | null
    sourceCountsLoading?: boolean
    showingCount: number
    showAddLead: boolean
    onAddLead?: () => void
}

export const LeadsRosterHeader: React.FC<LeadsRosterHeaderProps> = ({
    scope,
    onScopeChange,
    inLeadsCount,
    everyoneCount,
    query,
    onQueryChange,
    selectedSources,
    onSourcesChange,
    sourceCounts,
    sourceCountsLoading = false,
    showingCount,
    showAddLead,
    onAddLead,
}) => {
    const classes = useStyles()

    const scopeTotal = scope === 'in_leads' ? inLeadsCount : everyoneCount

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box className={classes.tiles}>
                    <Box
                        className={`${classes.tile} ${
                            scope === 'everyone' ? classes.tileActive : ''
                        }`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onScopeChange('everyone')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onScopeChange('everyone')
                            }
                        }}
                    >
                        <Typography className={classes.tileValue}>
                            {everyoneCount}
                        </Typography>
                        <Typography className={classes.tileLabel}>
                            Everyone
                        </Typography>
                        <Typography className={classes.tileHint}>
                            Full season roster
                        </Typography>
                    </Box>
                    <Box
                        className={`${classes.tile} ${
                            scope === 'in_leads' ? classes.tileActive : ''
                        }`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onScopeChange('in_leads')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onScopeChange('in_leads')
                            }
                        }}
                    >
                        <Typography className={classes.tileValue}>
                            {inLeadsCount}
                        </Typography>
                        <Typography className={classes.tileLabel}>
                            In Leads
                        </Typography>
                        <Typography className={classes.tileHint}>
                            Currently at this stage
                        </Typography>
                    </Box>
                </Box>
                <LeadSourcesChart
                    counts={sourceCounts}
                    selectedSources={selectedSources}
                    loading={sourceCountsLoading}
                    onSourcesChange={onSourcesChange}
                />
            </Box>

            <Box className={classes.tools}>
                <TextField
                    className={classes.search}
                    size="small"
                    variant="outlined"
                    placeholder="Search by name or email"
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="disabled" />
                            </InputAdornment>
                        ),
                    }}
                />

                <Typography className={classes.showing}>
                    Showing{' '}
                    <span className={classes.showingValue}>{showingCount}</span>
                    {showingCount !== scopeTotal
                        ? ` of ${scopeTotal}`
                        : ''}{' '}
                    {scope === 'in_leads' ? 'in Leads' : 'people'}
                </Typography>

                {showAddLead && onAddLead && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onAddLead}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Add lead
                    </Button>
                )}
            </Box>
        </Box>
    )
}
